import { AfterViewInit, Component, ElementRef, OnDestroy, OnInit, TemplateRef, ViewChild } from '@angular/core';
import { Router } from '@angular/router';
import { PopoverDirective } from 'ngx-bootstrap/popover';
import { isNullOrUndefined, isObject, isString } from '../../models/utils';
import { Endpoint, LocalStorageKey, MessageModalType } from '../../models/enums';
import { LoginData } from '../../models/login-data';
import { CommonService } from '../../services/common/common.service';
import { CookieService } from 'ngx-cookie-service';
import { HttpService } from '../../services/http/http.service';
import { RestService } from '../../services/rest/rest.service';
import { ChangePasswordModalComponent } from '../modals/change-password-modal/change-password-modal.component';
import * as jquery from 'jquery';
import * as _ from 'underscore';
import { EnterSecurityCodeModalComponent } from '../modals/enter-security-code-modal/enter-security-code-modal.component';
import { PolicyAcknowledgmentModalComponent } from '../modals/policy-acknowledgment-modal/policy-acknowledgment-modal.component';

@Component({
    standalone: false,
    selector: 'res-login',
    templateUrl: './login.component.html',
    styleUrls: ['./login.component.scss']
})
export class LoginComponent implements OnInit, OnDestroy, AfterViewInit {
    @ViewChild('dualPop') dualPop: PopoverDirective;
    @ViewChild('certificateTemplate', { static: true }) certificateTemplate: TemplateRef<any>;
    @ViewChild('aboutTemplate', { static: true }) aboutTemplate: TemplateRef<any>;
    @ViewChild('drVersion', { static: true }) drVersion: ElementRef;

    currentYear = new Date().getFullYear();
    username: string | null;
    password: string | null;
    rememberMe: boolean;
    passwordIsDisabled = false;
    mock = false;
    drCredentials: { username: string; password: string | null; rememberMeUserDeviceId: string | null; } | null;
    rememberMeBearerToken = null;
    passwordWasEdited = false;

    isBusy = false;
    isBusyTpAuth = false;
    isBusyMessage: string;

    isServerShown: boolean;
    url: string;
    deviceID: string;

    isStandardAuthentication = false;
    isTPAuthEnabledSystem = false;

    constructor(private commonService: CommonService,
        private httpService: HttpService,
        private restService: RestService,
        private router: Router,
        private cookieService: CookieService) {
        this.checkIfTpAuthEnabledSystem();
    }

    ngOnInit() {
        const sessionToken = this.cookieService.get(LocalStorageKey.TpAuthSessionToken);

        if (!this.isTPAuthEnabledSystem && sessionToken) {
            this.isTPAuthEnabledSystem = true;
        }
        this.isServerShown = window.location.hostname === 'localhost';
        this.url = this.commonService.debugServerIp;
        const tpauthLoginErrorMsg = this.cookieService.get(LocalStorageKey.TpAuthLoginError);
        const isTpauthLoginError = sessionToken ? false : tpauthLoginErrorMsg !== '';
        if (this.isTPAuthEnabledSystem || isTpauthLoginError) {
            if (isTpauthLoginError) {
                if (tpauthLoginErrorMsg.includes('access has been restricted')) {
                    this.commonService.openErrorModal("This account's access has been restricted.\n Please contact the administrator for assistance.", 'Account Restricted');
                }
                else {
                    this.commonService.openErrorModal(tpauthLoginErrorMsg.replace(/"/g, ''));
                }
                this.cookieService.delete(LocalStorageKey.TpAuthLoginError);
            } else {
                this.transPerfectAuthCodeFlow(sessionToken);
            }
        } else {
            this.drCredentials = this.commonService.getCacheData(LocalStorageKey.RememberMeWithToken);
            this.getAccessToken();

            this.loadRememberMeStoredInfo();
        }

        const loginInputData = this.commonService.getCacheData(LocalStorageKey.AppContext);
        if (loginInputData) {
            this.username = loginInputData.username;
            if (loginInputData.isTPAuthEnabledSystem) {
                this.login(loginInputData, loginInputData.sessionToken);
            } else {
                this.login(loginInputData);
            }
            this.commonService.setCacheData(LocalStorageKey.AppContext, null);
        }
    }

    ngAfterViewInit(): void {
        if (this.drVersion && this.drVersion.nativeElement.innerText === '@DR_VERSION@') {
            this.drVersion.nativeElement.innerText = 'Development Build';
        }
    }

    private checkIfTpAuthEnabledSystem(): void {
        this.httpService.get('assets/tpauth').subscribe({
            next: (content: string) => {
                if (content.trim().toLowerCase() === 'true') {
                    this.isTPAuthEnabledSystem = true;
                } else {
                    this.isStandardAuthentication = true;
                }
            },
            error: () => this.isStandardAuthentication = true
        });
    }

    private getAccessToken() {
        this.deviceID = this.commonService.getUuid(this.username);
    }

    private loadRememberMeStoredInfo(): void {
        if (this.areCredentialsStored() && this.drCredentials) {
            this.username = this.drCredentials.username;
            // password is a token, to be user friendly truncate it to minimum password lenght => 8 chars
            this.password = this.drCredentials.password?.slice(0, 8) ?? null;
            this.rememberMe = true;
            this.passwordIsDisabled = true;
        }
    }

    private transPerfectAuthCodeFlow(sessionToken: string): void {
        if (sessionToken) {
            this.cookieService.delete(LocalStorageKey.TpAuthSessionToken);
            this.isBusyTpAuth = true;
            this.isBusyMessage = 'Logging in ...';
            this.commonService.isTPAuthEnabledSystem = true;
            this.login(null, sessionToken);
        }
    }

    transPerfectAuthentication(): void {
        if (!localStorage.getItem(LocalStorageKey.IsTpAuthRedirect)) {
            localStorage.setItem(LocalStorageKey.IsTpAuthRedirect, 'loginRedirect');
        }
        window.open(this.commonService.nonRestUrl + '/ediscovery/secured', '_self');
    }

    areCredentialsStored(): boolean {
        return Boolean(this.drCredentials?.username && this.drCredentials?.password && this.drCredentials?.rememberMeUserDeviceId);
    }

    editStoredPassword(): void {
        if (this.passwordIsDisabled) {
            this.passwordIsDisabled = false;
            this.password = '';
            this.passwordWasEdited = true;
        }
    }

    login(inputData = null, sessionToken?: string): void {
        this.dualPop?.hide();
        this.isBusyMessage = 'Logging in ...';

        if (this.isServerShown) {
            this.commonService.debugServerIp = this.url;
        }

        if (!this.isTPAuthEnabledSystem) {
            this.getAccessToken();
        }

        this.username = this.username?.trim() ?? null;
        this.isBusy = true;
        const userHasStoredCredentials = this.areCredentialsStored() && this.drCredentials?.username === this.username && this.passwordIsDisabled;
        const loginInputData = inputData ? inputData : new LoginData({
            username: this.username, password: userHasStoredCredentials ? null : this.password,
            mfaDeviceId: this.deviceID, userDeviceId: userHasStoredCredentials ? this.drCredentials?.rememberMeUserDeviceId : null,
            clearStoredCredentials: this.passwordWasEdited,
            isRememberMeOn: this.rememberMe,
            bearerToken: userHasStoredCredentials ? this.drCredentials?.password : null,
            useMockServer: this.mock
        });

        this.commonService.appContext = loginInputData;

        this.restService.login(loginInputData, sessionToken ?? '')
            .then((response) => {
                this.isBusy = false;
                if (response.errorMessage && response.errorMessage.includes('The allowed number')) {
                    this.commonService.openErrorModal('The allowed number of login attempts has been exceeded. Your account has been \ntemporarily locked. Please contact your system administrator to unlock your account.');
                } else if (response.errorMessage && response.errorMessage.includes('Your IP address does not match') && this.commonService.currentUser.authenticationMethod !== 'EMAIL_CODE_AND_NOT_IP') {
                    this.commonService.openErrorModal(response.errorMessage);
                } else {
                    let rememberMeToken = response?.bearerToken;
                    if (this.drCredentials && this.drCredentials.username === this.username && !rememberMeToken) {
                        rememberMeToken = this.drCredentials.password;
                    }
                    this.rememberMeBearerToken = rememberMeToken;
                    if (response.needSecurityCode) {
                        this.openSecurityCodeModal(response);
                    } else {
                        this.navigateToProjectList();
                    }
                }
            }, error => {
                this.isBusy = false;
                if (isObject(error) && isString(error.error) && error.error.includes('The allowed number')) {
                    this.commonService.openErrorModal('The allowed number of login attempts has been exceeded. Your account has been \ntemporarily locked. Please contact your system administrator to unlock your account.');
                } else if (isObject(error) && isString(error.error) && error.error.includes('access to this application')) {
                    this.commonService.openErrorModal('You do not have access to this application.');
                } else if (isObject(error) && isString(error.error) && error.error.includes('Your remembered session')) {
                    this.clearRememberMeInfo(false);
                    this.commonService.openErrorModal('Your remembered session has expired on this device.\n\rYou will need to re-enter your password to log in even if you had the "Remember Me" feature enabled.');
                } else if (isObject(error) && isString(error.error) && error.error.includes('access has been restricted')) {
                    this.commonService.openErrorModal("This account's access has been restricted.\n Please contact the administrator for assistance.", 'Account Restricted');
                } else if (error.status === 500 && ((isObject(error) && isString(error.error) && !error.error.includes('The allowed number')))) {
                    this.dualPop?.show();
                } else if (error?.extendedStatus?.includes('User is not logged in')) { // remember me credentials are not valid anymore
                    this.clearRememberMeInfo();
                    this.commonService.showLogoutOnAllDevicesInfoMessage();
                } else {
                    this.commonService.openErrorModal(error);
                }
            });
    }

    private clearRememberMeInfo(clearUsername = true): void {
        this.commonService.clearRememberMeDataFromLocalStorage();
        this.drCredentials = null;
        this.password = null;
        if (clearUsername) {
            this.username = null;
        }
        this.rememberMe = false;
        this.rememberMeBearerToken = null;
        this.passwordIsDisabled = false;
        this.passwordWasEdited = false;
    }

    private navigateToProjectList() {
        if (!this.isTPAuthEnabledSystem && this.commonService.currentUser.daysUntilPasswordExpires <= 0) {
            this.commonService.openConfirmationModal(
                'Your password expired, a password change has been requested, or this is your first time logging in.\n\nPlease change your password on the next screen.',
                'Password change required', 'Continue', 'Cancel'
            ).then((result) => {
                if (result && result.modalResult) {
                    this.changePassword(true);
                }
            });
        } else {
            if (this.commonService.policyAcknowledgment) {
                this.handleRememberMe();
                this.httpService.startPoller();
                this.router.navigate(['project-list'], { skipLocationChange: true });
                this.displaySystemMessage();
            } else {
                this.commonService.openModal(PolicyAcknowledgmentModalComponent).then((result) => {
                    if (result.modalResult) {
                        this.handleRememberMe();
                        this.httpService.startPoller();
                        this.router.navigate(['project-list'], { skipLocationChange: true });
                        this.displaySystemMessage();
                        this.policyAcknowledgmentApproved();
                    } else {
                        this.isBusyTpAuth = false;
                    }
                });
            }
        }
    }

    private policyAcknowledgmentApproved(): void {
        this.restService.post(Endpoint.userManager_acceptEula, { ediscoveryEula: true }).then(response => {
            if (response && response.errorCode) {
                this.commonService.openErrorModal(response.extendedStatus);
            }
        });
    }

    private displaySystemMessage() {
        this.restService.getSplashMessage().subscribe((response) => {
            if (response) {
                const xmlResponse = jquery.parseXML(response);
                if (xmlResponse?.documentElement) {
                    const children = xmlResponse.documentElement.children;
                    const enabled = _.find(children, (node) => node.nodeName === 'enabled');
                    if (enabled?.textContent === 'true') {
                        const message = _.find(children, (node) => node.nodeName === 'message');
                        if (message) {
                            this.commonService.openInformationToastNotification(message.textContent.replace(/\n/g, '<br/>'));
                        }
                    }
                }
            }
        });
    }

    changePassword(redirect = false): void {
        this.commonService.openModal(ChangePasswordModalComponent, {
            backdrop: false,
            class: 'horizontal-px-500',
            modalData: { loginUserName: this.username }
        }).then(result => {
            if (redirect && result && result.modalResult && isString(result.modalData)) {
                this.password = result.modalData;
                this.login();
            }
        });
    }

    usernameOnChange(): void {
        const isSameUserAsStoredOne = this.username === this.drCredentials?.username;
        if (this.passwordIsDisabled && !isSameUserAsStoredOne) {
            this.passwordIsDisabled = false;
            this.password = null;
            return;
        }
        if (isSameUserAsStoredOne && !this.passwordIsDisabled) {
            this.passwordWasEdited = false;
        }
    }

    handleRememberMe(): void {
        if (this.rememberMe && this.rememberMeBearerToken) {
            const drCredentials = { username: this.username, password: this.rememberMeBearerToken, rememberMeUserDeviceId: this.commonService.currentUser?.deviceId };
            this.commonService.setCacheData(LocalStorageKey.RememberMeWithToken, drCredentials);
        } else {
            this.commonService.clearRememberMeDataFromLocalStorage();
        }
    }

    verifyFields() {
        return this.username === '' || isNullOrUndefined(this.username) || this.password === '' || isNullOrUndefined(this.password);
    }

    openCertificateModal(): void {
        this.commonService.openCustomContentMessageModal(this.certificateTemplate, MessageModalType.confirmation, 'Certificate Authority (CA) Installation', '', '', '', 'cert-modal-container');
    }

    openAboutModal = () => {
        this.commonService.openCustomContentMessageModal(this.aboutTemplate, MessageModalType.information, 'About Reef ECA', '', '', '', 'about-modal-container');
    }

    openSecurityCodeModal(user): void {
        this.commonService.openModal(EnterSecurityCodeModalComponent, { backdrop: false, modalData: { user: user } }).then(result => {
            if (result && result.modalResult) {
                this.navigateToProjectList();
            }
        });
    }

    ngOnDestroy(): void {
        if (localStorage.getItem(LocalStorageKey.IsTpAuthRedirect) === 'loginRedirect') {
            localStorage.removeItem(LocalStorageKey.IsTpAuthRedirect);
        }
    }
}
